/**
 * product-garment.js -- inline per-size garment measurements on the PDP.
 *
 * Renders alongside the size chip row (`snippets/product-garment-measurements.liquid`,
 * inline mode). The snippet emits a `data-garment` JSON payload of every
 * size's garment measurements; this script swaps the visible line to match
 * whichever size the shopper currently has selected.
 *
 * Data shape (per the snippet):
 *   [{ "size": "AU 8", "measures": [{ "label": "Chest", "cm": "86", "in": "33.9" }, ...] }, ...]
 *
 * Size matching: the size selector chips carry `data-option-value` (the raw
 * option string, e.g. "AU 8"). We match it case-insensitively against each
 * payload row's `size`, with a contains fallback so a chip value of "8" can
 * still resolve a row labelled "AU 8 / US 4".
 *
 * Unit: mirrors the size-guide drawer's CM/IN choice, persisted in
 * localStorage under `size-unit`. Defaults to cm. We also react to the
 * drawer's unit toggle so the inline line stays in sync while open.
 *
 * Degrades silently: no payload, no rows, or no match -> the line stays
 * hidden. No framework; vanilla JS, defer-loaded once per PDP.
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'size-unit';

  function readUnit() {
    try {
      var v = window.localStorage.getItem(STORAGE_KEY);
      if (v === 'cm' || v === 'in') return v;
    } catch (err) {
      /* localStorage unavailable -- fall through */
    }
    return 'cm';
  }

  function parsePayload(root) {
    var raw = root.getAttribute('data-garment');
    if (!raw) return [];
    try {
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  }

  function findRow(rows, value) {
    if (!value) return null;
    var needle = String(value).trim().toLowerCase();
    var i;
    for (i = 0; i < rows.length; i++) {
      if (String(rows[i].size).trim().toLowerCase() === needle) return rows[i];
    }
    for (i = 0; i < rows.length; i++) {
      var haystack = String(rows[i].size).trim().toLowerCase();
      if (haystack.indexOf(needle) !== -1 || needle.indexOf(haystack) !== -1) {
        return rows[i];
      }
    }
    return null;
  }

  function render(root, valuesEl, rows, value) {
    var row = findRow(rows, value);
    if (!row || !row.measures || !row.measures.length) {
      root.setAttribute('hidden', '');
      valuesEl.textContent = '';
      return;
    }

    var unit = readUnit();
    var parts = [];
    for (var i = 0; i < row.measures.length; i++) {
      var m = row.measures[i];
      var amount = unit === 'in' ? m.in : m.cm;
      if (amount === null || amount === undefined || amount === '') continue;
      parts.push(m.label + ' ' + amount + (unit === 'in' ? '"' : 'cm'));
    }

    if (!parts.length) {
      root.setAttribute('hidden', '');
      valuesEl.textContent = '';
      return;
    }

    valuesEl.textContent = parts.join('  /  ');
    root.removeAttribute('hidden');
  }

  function selectedValue(fieldset) {
    var checked = fieldset.querySelector('.product-size-selector__input:checked');
    if (checked) return checked.getAttribute('data-option-value');
    return null;
  }

  function mount(root) {
    if (root.hasAttribute('data-product-garment-mounted')) return;
    root.setAttribute('data-product-garment-mounted', '');

    var valuesEl = root.querySelector('[data-product-garment-values]');
    if (!valuesEl) return;

    var rows = parsePayload(root);
    if (!rows.length) return;

    var scope =
      root.closest('[data-section-type="product-info"]') || document;
    var fieldset = scope.querySelector('[data-product-size-selector]');
    if (!fieldset) return;

    function update() {
      render(root, valuesEl, rows, selectedValue(fieldset));
    }

    fieldset.addEventListener('change', update);

    // Keep in sync with the drawer's CM/IN toggle while it is open.
    document.addEventListener('click', function (event) {
      var unitBtn = event.target.closest('[data-size-guide-unit]');
      if (unitBtn) {
        window.setTimeout(update, 0);
      }
    });

    update();
  }

  function init() {
    document.querySelectorAll('[data-product-garment]').forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
